// Menu examples arrive with the matches marked by the server, as in MT Admin (markedText.ts):
//   <b> - primary match: the keyword and its include extensions (only the server knows them);
//   <i> - secondary match: what the search term hit. A run can carry both.
// The markup is split into runs and never rendered as HTML; any other tag is dropped.

export interface MarkedSegment {
    text: string;
    isMatch: boolean;
    isSecondary: boolean;
}

type MarkTag = 'b' | 'i';

const isMarkTag = (name: string): name is MarkTag => name === 'b' || name === 'i';

const TAG_PATTERN = /<(\/?)([a-zA-Z][^\s/>]*)[^>]*>/g;

const ENTITY_PATTERN = /&(amp|lt|gt|quot|#39|apos);/g;
const ENTITIES: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    '#39': "'",
    apos: "'",
};

const decodeEntities = (text: string): string =>
    text.replace(ENTITY_PATTERN, (match, name: string) => ENTITIES[name] ?? match);

export function parseMarkedText(raw: string): MarkedSegment[] {
    const segments: MarkedSegment[] = [];
    // Depths rather than booleans: nested marks close correctly and a stray closing tag can't go negative.
    const depths: Record<MarkTag, number> = { b: 0, i: 0 };

    // Adjacent runs with the same flags are merged, so a dropped tag doesn't split plain text.
    const push = (text: string) => {
        if (text === '') {
            return;
        }
        const isMatch = depths.b > 0;
        const isSecondary = depths.i > 0;
        const last = segments[segments.length - 1];
        if (last && last.isMatch === isMatch && last.isSecondary === isSecondary) {
            last.text += text;
            return;
        }
        segments.push({ text, isMatch, isSecondary });
    };

    const pattern = new RegExp(TAG_PATTERN.source, 'g');
    let cursor = 0;
    let tag = pattern.exec(raw);

    while (tag !== null) {
        push(decodeEntities(raw.slice(cursor, tag.index)));
        const [matched, slash, name] = tag;
        const tagName = name.toLowerCase();
        if (isMarkTag(tagName)) {
            depths[tagName] = slash === '/' ? Math.max(0, depths[tagName] - 1) : depths[tagName] + 1;
        }
        cursor = tag.index + matched.length;
        tag = pattern.exec(raw);
    }

    // An unclosed mark covers the rest of the text rather than losing the match.
    push(decodeEntities(raw.slice(cursor)));

    return segments;
}
